import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { Badge } from '@BICE_Vida/scp-components';
import styles from './PlansComparisonTable.module.scss';
import PlansComparisonTableHeader from './PlansComparisonTableHeader';
import { AvailablePlan, AvailablePlans } from '@/src/types/availablePlans';
import { UF } from '@/src/types/uf';
import {
  AvailablePlans as AvailablePlansType,
  AvailablePlan as AvailablePlanType,
} from '../../types/availablePlans';
import * as plansSelectors from '@/redux/plans/selectors';
import { Checkbox } from '@/designSystem/Checkbox';
import AvailablePlanModal from '../AvailablePlanModal';
import { comparePlansTexts, landingText } from '@/utils/generalTexts';
import { coveragesList, generateCoverageRows } from '@/utils/comparePlans';
import { capitalizeString, formatMoney } from '@/utils/format';
import { variableOrder } from '@/utils/constants';
import { trackClick } from '@/utils/dataLayer/home';

type BrandingT = {
  code: string;
  primary: string;
  character: string;
  logo: string;
  promo: boolean;
};

type Props = {
  plans: AvailablePlans;
  UF: UF;
  customClass?: string;
  btnAction: {
    id: string;
    text: string;
    click?: () => void;
    onClickEvent?: () => void;
  };
  branding?: BrandingT;
};

type PlanChecked = {
  dental: boolean;
  catastrofico: boolean;
  deceso: boolean;
};

type AvailablePlanExtend = AvailablePlan & PlanChecked;

const PlansComparisonTable: React.FC<Props> = ({ plans, UF, customClass, btnAction, branding }) => {
  const [plansChecked, setPlansChecked] = useState<AvailablePlanExtend[]>();
  const dateFormatted = `${UF.day}/${UF.month}/${UF.year}`;

  useEffect(() => {
    if (plans && plans.length > 0) {
      const initializedPlans = plans.map((plan) => ({
        ...plan,
        catastrofico: false,
        dental: false,
        deceso: false,
      }));
      setPlansChecked(initializedPlans);
    }
  }, [plans]);

  const [index, setIndex] = useState<number>(0);
  const [loadingColumn, setLoadingColumn] = useState<number | null>(null);

  const availablePlans: AvailablePlansType = useSelector(plansSelectors.getAvailablePlans);
  const [showPlanDetails, setShowPlanDetails] = useState(false);

  if (!plans.length) return null;

  const openPlanDetailsModal = () => {
    setShowPlanDetails(true);
    btnAction?.onClickEvent?.();
  };

  const closePlanDetailsModal = () => setShowPlanDetails(false);

  const ufCoverages = availablePlans
    .filter(
      (plan) =>
        plan.description === 'Pyme Estándar + dental' ||
        plan.description === 'Pyme Estándar + catastrófico' ||
        plan.description === 'Pyme Estándar + deceso'
    )
    .map((plan) => {
      if (plan.coverage.catastrophic) {
        return { name: 'catastrofico', uf: plan.price.uf - availablePlans[0].price.uf };
      }

      if (plan.coverage.decease) {
        return { name: 'deceso', uf: plan.price.uf - availablePlans[0].price.uf };
      }

      return { name: 'dental', uf: plan.price.uf - availablePlans[0].price.uf };
    })
    .sort((a, b) => coveragesList.indexOf(a.name) - variableOrder.indexOf(b.name));

  const getVariantByCoverage = (
    plans: AvailablePlanType[],
    { dental, catastrofico, deceso }: { dental: boolean; catastrofico: boolean; deceso: boolean }
  ): AvailablePlanType | undefined => {
    return plans.find((plan) => {
      const hasDental = plan.coverage.dental !== null;
      const hasCatastrofico = plan.coverage.catastrophic !== null;
      const hasDeceso = plan.coverage.decease !== null;

      return hasDental === dental && hasCatastrofico === catastrofico && hasDeceso === deceso;
    });
  };

  const handleCheck = (planChecked: AvailablePlanExtend, coverage: string, idx: number) => {
    const filteredPlansByType = availablePlans.filter((plan) => plan.type === planChecked.type);

    const isCurrentlySelected = planChecked[coverage as keyof PlanChecked];

    const newCoverageState = {
      dental: coverage === 'dental' ? !planChecked.dental : planChecked.dental,
      catastrofico:
        coverage === 'catastrofico' ? !planChecked.catastrofico : planChecked.catastrofico,
      deceso: coverage === 'deceso' ? !planChecked.deceso : planChecked.deceso,
    };

    const selectedVariant = getVariantByCoverage(filteredPlansByType, newCoverageState);

    setPlansChecked((prevPlans) =>
      (prevPlans ?? []).map((plan) =>
        plan._id === planChecked._id
          ? {
              ...plan,
              ...newCoverageState,
              price: { uf: selectedVariant?.price.uf ?? 0 },
            }
          : plan
      )
    );

    if (!isCurrentlySelected) {
      trackClick({
        action: `Click Checkbox – Seguro ${capitalizeString(coverage)} – Comparar planes`,
        productType: planChecked.description,
      });
    }

    setLoadingColumn(idx);
    setTimeout(() => setLoadingColumn(null), 500);
  };

  const coverageRows = generateCoverageRows(plansChecked ?? []);

  const coveragesListVar = ufCoverages.length === 2 ? coveragesList.slice(0, 2) : coveragesList;

  return (
    <div className={styles.table_wrapper}>
      <AvailablePlanModal
        isOpen={showPlanDetails}
        handleClose={closePlanDetailsModal}
        plan={plans[index]}
        coverages={plans[index].coverage}
        UF={UF}
        openPDF={btnAction?.click}
        theme="secondary"
        brandingCode={branding?.code}
      />
      <table className={styles.comparison_table}>
        <thead className={styles.table_header}>
          <tr>
            <th />
            {(plansChecked ?? []).map((availablePlan, idx) => {
              const customClasses = classNames(
                styles.available_plan_card,
                {
                  [`${customClass}`]: customClass,
                },
                availablePlan.features?.includes('dark') && styles.dark_card
              );

              return (
                <th className={customClasses} key={`${availablePlan._id}-plan`}>
                  <PlansComparisonTableHeader
                    setIndex={setIndex}
                    idx={idx}
                    openPlanDetailsModal={openPlanDetailsModal}
                    availablePlan={availablePlan}
                    UF={UF}
                    isLoading={loadingColumn === idx}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {coverageRows.map((row) => (
            <tr key={row.description}>
              <td className={styles.item_description_first_col}>{row.description}</td>
              {(plansChecked ?? []).map((plan) => {
                const value = row.values[plan._id];

                return (
                  <td key={plan._id}>
                    {row.rowType === 'boolean' && (
                      <span
                        className={styles.icon}
                        aria-label={value === '✔️' ? 'Incluido' : 'No incluido'}
                      >
                        {value}
                      </span>
                    )}

                    {(row.rowType === 'percentage_maximum' ||
                      row.rowType === 'maximum' ||
                      row.rowType === 'fixed' ||
                      row.rowType === 'text') && <span className={styles.others_col}>{value}</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tr className={styles.footer_title}>
          <td colSpan={(plansChecked?.length ?? 0) + 1}>{comparePlansTexts.coverageTextFooter}</td>
        </tr>
        <tfoot>
          {coveragesListVar.map((coverage, key) => (
            <tr key={key}>
              <td className={styles.item_description_first_col}>
                <p className={styles.item_description_first_col_text}>
                  {`Seguro ${
                    coverage === 'catastrofico' ? 'Catastrófico' : capitalizeString(coverage)
                  }`}

                  {coverage === 'deceso' && (
                    <Badge
                      className={styles.badge_title}
                      text="Solo titulares"
                      colorBg="#FFF0B3"
                      colorText="#775A06"
                    />
                  )}
                </p>
                <p className={styles.item_subtitle_first_col}>
                  {comparePlansTexts.fromCoverages(ufCoverages[key]?.uf, UF?.value, coverage)}
                </p>
              </td>
              {(plansChecked ?? []).map((planChecked, idx) => (
                <td key={`${planChecked._id}-${key}`}>
                  <Checkbox
                    id={`${planChecked._id}-${coverage}`}
                    customClass={styles.card_selectable_selector}
                    onChange={() => handleCheck(planChecked, coverage, idx)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
      <div className={styles.available_plans_legend_section}>
        <p className={styles.disclaimer_text}>
          {landingText.coverages.warningUF(formatMoney(UF.value), dateFormatted)}
        </p>
      </div>
    </div>
  );
};

export default PlansComparisonTable;
